import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { SETTINGS_GROUPS, type Tab } from '@/src/components/admin/navConfig';

type Props = {
  activeTab: Tab;
  onSelect: (tab: Tab) => void;
  children: ReactNode;
};

/**
 * The settings hub: everything an owner configures once and rarely revisits.
 * The rail is desktop-only — on a phone the drawer already lists every leaf,
 * so a second nav layer would just eat the screen.
 */
export default function AdminSettingsLayout({ activeTab, onSelect, children }: Props) {
  return (
    <div>
      {/* Mobile: the way back. Each leaf already names itself in its own card
          header, so repeating the section title here would just be noise. */}
      <div className="mb-4 md:hidden">
        <button
          onClick={() => onSelect('calendar')}
          className="hover:text-accent text-fg-muted flex items-center gap-1.5 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to calendar
        </button>
      </div>

      <h1 className="text-fg mb-6 hidden text-2xl font-bold md:block">Settings</h1>

      <div className="flex flex-col gap-8 md:flex-row">
        {/* Sticky so the rail stays reachable down the 900-line Salon page. */}
        <nav
          className="hidden w-56 shrink-0 self-start md:sticky md:top-20 md:block"
          aria-label="Settings"
        >
          {SETTINGS_GROUPS.map((group) => (
            <div key={group.heading} className="mb-6 last:mb-0">
              <p className="text-fg-subtle px-3 pb-1 text-xs font-semibold tracking-wider uppercase">
                {group.heading}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = item.id === activeTab;
                  return (
                    <button
                      key={item.id}
                      onClick={() => onSelect(item.id)}
                      aria-current={active ? 'page' : undefined}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        active
                          ? 'bg-accent-soft text-accent'
                          : 'text-fg-secondary hover:bg-muted hover:text-fg'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}
